import { amorphicStatic } from '@haventech/amorphic';
import { ProductClient } from '@hit/product-client';
import { ProductApiTypes } from '@hit/policy-admin-types';

import { Product } from '../static/Product';
import { AgeUtils } from '../static/AgeUtils';
import { CoverpathProductClientCtx } from '../productService/CoverpathProductClientCtx';
import { Utils } from '../Utils';
import { UwFlatExtra } from '../../../common/js/interfaces/UwFlatExtra';
import { HavenStats } from '../havenStats/HavenStats';
import { LoggerSingleton } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

export interface PremiumServiceRequest {
  inquiries: PremiumInquiry[];
  context?: PremiumExecutionContext | ProductApiTypes.ContextReq;
}

export interface PremiumServiceResponse {
  premiums: Premium[];
}

interface PremiumExecutionContext {
  userId?: string;
  requestId?: string;
  referenceId?: string;
  referenceType?: string;
  eventName?: string;
}

// Premium Service doesn't support directly 'unisex'
// on the 'gender' property, because Product Service
// uses the state for those cases.
export interface PremiumInquiry {
  product: string;
  gender: ProductApiTypes.Gender;
  rateClassCode: string;
  term: number;
  face: number;
  state: string;
  dob: Date;
  paymentFrequency: ProductApiTypes.PaymentFrequency;
  version?: string;
  productType?: ProductApiTypes.ProductType;
  effectiveDate?: Date;
  includeWaiver?: boolean;
  flatExtras?: UwFlatExtra[];
}

export interface Premium {
  version: string;
  base: number;
  face: number;
  term: number;
  product: ProductApiTypes.ProductCode;
  waiver?: number;
  flatExtras?: Array<FlatExtraResponse>;
  rateClass?: string;
}

interface FlatExtraResponse {
  name: string;
  premium: number;
}

interface ProductServiceResponse {
  data: {
    premiums: ProductApiTypes.PremiumResponse;
  };
}

export class PremiumService {
  private static readonly PRODUCT_SERVICE_PREMIUM_QUERY = {
    payload: {
      policies: [
        {
          duration: null,
          version: null,
          totalUnits: null,
          product: {
            code: null,
          },
          segments: [
            {
              benefits: [
                {
                  code: null,
                  basePremium: null,
                  tableRatingPremium: null,
                  fee: null,
                  flatExtras: [
                    {
                      name: null,
                      premium: null,
                    },
                  ],
                },
              ],
              rating: {
                rateClass: null,
              },
            },
          ],
        },
      ],
    } as ProductApiTypes.PremiumPayload,
  };

  static async getPremiums(request: PremiumServiceRequest): Promise<PremiumServiceResponse> {
    const payload = PremiumService._buildProductPayload(request);
    const context = request.context;
    const randomUUID = Utils.generateUuidV4();
    const globalHeaders = LoggerSingleton.getInstance()?.getContextFromLocalStorage();

    const msgCtx = {
      userId: (context && context.userId) || amorphicStatic.config.nconf.get('APPLICATION') || 'PremiumService',
      requestId: (context && context.requestId) || randomUUID,
      referenceId: (context && context.referenceId) || randomUUID,
      referenceType: (context && context.referenceType) || 'UUID',
      eventName: context && context.eventName,
      globalId: globalHeaders?.globalId,
      globalSource: globalHeaders?.globalSource,
    };

    const productClientCtx = CoverpathProductClientCtx.new();
    const ctx = productClientCtx.newExecutionCtx(msgCtx);
    productClientCtx.exnCtx = ctx;
    try {
      const requestTime = process.hrtime();
      HavenStats.singletonStatsDClient().increment('PremiumService.getPremiums.request.count');
      productClientCtx.config.AUTH_TOKEN = amorphicStatic.config.nconf.get('cpApiGatewayJWTToken');
      const premiumResponse = await ProductClient.new(productClientCtx).getPremium(
        payload,
        PremiumService.PRODUCT_SERVICE_PREMIUM_QUERY
      );

      if (!premiumResponse) {
        throw new Error('No premiums were received from Product Service.');
      }

      const timeDiffInMilliseconds = Utils.getTimeOffset(requestTime);
      HavenStats.singletonStatsDClient().timing('PremiumService.getPremiums.request.time', timeDiffInMilliseconds, {
        unit: 'milliseconds',
      });

      return PremiumService._formatProductResponse(premiumResponse);
    } catch (e) {
      HavenStats.singletonStatsDClient().increment('PremiumService.getPremiums.error.count');
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          msgCtx,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'PremiumService',
          function: 'getPremiums',
          category: UICategory.request,
          error: err,
        },
        `Error retrieving premium from Product Service. ${err.message}`
      );
      throw err;
    }
  }

  static _buildProductPayload(request: PremiumServiceRequest): ProductApiTypes.PremiumPayloadReq {
    const now = new Date();

    const policies = request.inquiries.map((inquiry) => {
      const flatExtras =
        inquiry.flatExtras &&
        inquiry.flatExtras.map((flatExtra) => {
          return {
            name: flatExtra.ruleCode,
            rate: flatExtra.amount,
          };
        });

      const benefits: ProductApiTypes.BenefitReq[] = [
        {
          code: ProductApiTypes.BenefitCode.Death,
          units: inquiry.face,
          flatExtras,
        },
      ];

      if (inquiry.includeWaiver) {
        benefits.push({
          code: ProductApiTypes.BenefitCode.Waiver,
          units: inquiry.face,
        });
      }

      // Change productGroup from Life to Term to build the correct hierarchy for workflow metadata
      // TODO: The line below should be removed, after productGroup for workflow metadata is changed from Term to Life
      let productsTypeGroupName = Product.products[inquiry.product].type.group.name;
      productsTypeGroupName = productsTypeGroupName === 'LIFE' ? 'Term' : productsTypeGroupName;

      const age = AgeUtils.getInsuranceAge(inquiry.dob);
      if (
        age > Product.productSpecs[inquiry.product].maxAgeForHigherTerm &&
        inquiry.term > Product.productSpecs[inquiry.product].maxTermForHigherAge
      ) {
        throw { message: `Term ${inquiry.term} years is not available for customer at age ${age}` };
      }

      return {
        version: inquiry.version,
        asOfDate: inquiry.effectiveDate || now,
        startDate: inquiry.effectiveDate || now,
        duration: inquiry.term,
        totalUnits: inquiry.face,
        paymentFrequency: inquiry.paymentFrequency,
        insured: {
          dob: inquiry.dob,
          gender: inquiry.gender,
        },
        product: {
          type: inquiry.productType || productsTypeGroupName,
          code: ProductApiTypes.ProductCode[inquiry.product],
          state: ProductApiTypes.State[inquiry.state],
        },
        segments: [
          {
            id: '1',
            startDate: inquiry.effectiveDate || now,
            rating: {
              rateClass: inquiry.rateClassCode,
            },
            benefits,
          },
        ],
      };
    });

    return { policies };
  }

  static _formatProductResponse(response: ProductServiceResponse): PremiumServiceResponse {
    const premiums = response.data.premiums.payload.policies.map((policy) => {
      // It can rely in that Product Service will provide only one segment at
      // this current level. Multiple segments are provided on post issues scenarios.
      const segment = policy.segments[0];

      const premium = {
        version: policy.version || '0',
        base: 0,
        waiver: 0,
        face: policy.totalUnits,
        term: policy.duration,
        product: policy.product.code,
        flatExtras: [],
        rateClass: (segment && segment.rating && segment.rating.rateClass) || '',
      };

      segment.benefits.forEach((benefit) => {
        switch (benefit.code) {
          case ProductApiTypes.BenefitCode.Death:
            const flatExtras = benefit.flatExtras;

            premium.base = benefit.basePremium + benefit.fee + benefit.tableRatingPremium;

            if (flatExtras && flatExtras.length) {
              premium.flatExtras = flatExtras.map((flatExtra) => {
                return {
                  name: flatExtra.name,
                  premium: flatExtra.premium,
                };
              });
            }

            break;
          case ProductApiTypes.BenefitCode.Waiver:
            premium.waiver = benefit.basePremium;
            break;
        }
      });

      return premium;
    });

    return { premiums };
  }
}
